import type { ComponentType } from "react";
import DatabricksWorkspacesIntegrationForm from "./components/SystemSettingsIntegrations/components/DatabricksWorkspacesIntegrationForm/DatabricksWorkspacesIntegrationForm";
import {
  type DatabricksWorkspaceEntry,
  emptyDatabricksWorkspace,
  parseWorkspaces,
} from "./components/SystemSettingsIntegrations/components/DatabricksWorkspacesIntegrationForm/DatabricksWorkspacesIntegrationForm.types";
import { canSaveDatabricks } from "./components/SystemSettingsIntegrations/components/DatabricksWorkspacesIntegrationForm/DatabricksWorkspacesIntegrationForm.utils";
import DataprocStagingIntegrationForm from "./components/SystemSettingsIntegrations/components/DataprocStagingIntegrationForm/DataprocStagingIntegrationForm";
import {
  type DataprocStagingEntry,
  parseDataprocStagingIntegrations,
} from "./components/SystemSettingsIntegrations/components/DataprocStagingIntegrationForm/DataprocStagingIntegrationForm.types";
import {
  canSaveDataproc,
  emptyDataprocStaging,
} from "./components/SystemSettingsIntegrations/components/DataprocStagingIntegrationForm/DataprocStagingIntegrationForm.utils";
import EmrAutoTuneIntegrationForm from "./components/SystemSettingsIntegrations/components/EmrAutoTuneIntegrationForm/EmrAutoTuneIntegrationForm";
import {
  type EmrAutoTuneEntry,
  parseEmrAutoTuneIntegrations,
} from "./components/SystemSettingsIntegrations/components/EmrAutoTuneIntegrationForm/EmrAutoTuneIntegrationForm.types";
import {
  canSaveEmrAutoTune,
  emptyEmrAutoTune,
} from "./components/SystemSettingsIntegrations/components/EmrAutoTuneIntegrationForm/EmrAutoTuneIntegrationForm.utils";
import EmrStagingIntegrationForm from "./components/SystemSettingsIntegrations/components/EmrStagingIntegrationForm/EmrStagingIntegrationForm";
import {
  type EmrStagingEntry,
  parseEmrStagingIntegrations,
} from "./components/SystemSettingsIntegrations/components/EmrStagingIntegrationForm/EmrStagingIntegrationForm.types";
import {
  canSaveEmr,
  emptyEmrStaging,
} from "./components/SystemSettingsIntegrations/components/EmrStagingIntegrationForm/EmrStagingIntegrationForm.utils";
import GithubIntegrationForm from "./components/SystemSettingsIntegrations/components/GithubIntegrationForm/GithubIntegrationForm";
import {
  type GithubIntegrationEntry,
  parseGithubIntegrations,
} from "./components/SystemSettingsIntegrations/components/GithubIntegrationForm/GithubIntegrationForm.types";
import {
  canSaveGithub,
  emptyGithubIntegration,
} from "./components/SystemSettingsIntegrations/components/GithubIntegrationForm/GithubIntegrationForm.utils";

/** The only shape the shared table needs from any integration entry. */
export interface IntegrationEntry {
  enabled: boolean;
}

export interface IntegrationTypeDef {
  type: string;
  label: string;
  dialogTitle: string;
  /** TenantSettings field (and tenant_settings jsonb column) holding the array. */
  field: string;
  parse: (raw: unknown) => IntegrationEntry[];
  /** New object every call. Databricks includes a fresh nested `auth`. */
  empty: () => IntegrationEntry;
  // ponytail: `any` at this seam only — each type's entry differs, and a
  // precise generic here costs more type gymnastics than it buys.
  canSave: (entry: any) => boolean;
  toPayload: (entry: any) => IntegrationEntry;
  Fields: ComponentType<{ entry: any; onChange: (entry: any) => void }>;
  /** How one entry renders in the shared table. */
  row: (entry: any) => { name: string | null; detail: string };
  /** The backend has a "Test connection" check for this type. */
  testable?: boolean;
  /** Most entries allowed; Add is disabled once reached. Unset = unlimited. */
  maxEntries?: number;
}

const identityPayload = (entry: any) => entry;

export const INTEGRATION_TYPES: IntegrationTypeDef[] = [
  {
    type: "databricks",
    label: "Databricks",
    dialogTitle: "Databricks Workspace",
    field: "databricks_workspaces",
    parse: parseWorkspaces,
    empty: emptyDatabricksWorkspace,
    canSave: canSaveDatabricks,
    toPayload: identityPayload,
    Fields: DatabricksWorkspacesIntegrationForm,
    testable: true,
    // A workspace has nothing to name — its URL is the identity.
    row: (entry: DatabricksWorkspaceEntry) => ({
      name: null,
      detail: entry.url,
    }),
  },
  {
    type: "github",
    label: "GitHub",
    dialogTitle: "GitHub Integration",
    field: "github_integrations",
    parse: parseGithubIntegrations,
    empty: emptyGithubIntegration,
    canSave: canSaveGithub,
    toPayload: identityPayload,
    Fields: GithubIntegrationForm,
    testable: true,
    row: (entry: GithubIntegrationEntry) => ({
      name: entry.name,
      detail: entry.url,
    }),
  },
  {
    type: "dataproc_staging",
    label: "Dataproc Staging",
    dialogTitle: "Dataproc Staging Integration",
    field: "dataproc_staging_integrations",
    parse: parseDataprocStagingIntegrations,
    empty: emptyDataprocStaging,
    canSave: canSaveDataproc,
    toPayload: identityPayload,
    Fields: DataprocStagingIntegrationForm,
    // No URL — the project is the name and the pinned runner is what to eyeball.
    row: (entry: DataprocStagingEntry) => ({
      name: entry.project_id,
      detail: entry.run_as_service_account,
    }),
  },
  {
    type: "emr_staging",
    label: "EMR Staging",
    dialogTitle: "EMR Staging Integration",
    field: "emr_staging_integrations",
    parse: parseEmrStagingIntegrations,
    empty: emptyEmrStaging,
    canSave: canSaveEmr,
    toPayload: (entry: EmrStagingEntry) => ({
      ...entry,
      runner_role_arn: entry.runner_role_arn.trim(),
    }),
    Fields: EmrStagingIntegrationForm,
    row: (entry: EmrStagingEntry) => ({
      name: entry.runner_role_arn,
      detail: entry.base_location,
    }),
  },
  {
    type: "emr_autotune",
    label: "EMR Auto-Tune",
    dialogTitle: "EMR Auto-Tune Integration",
    field: "emr_autotune_integrations",
    parse: parseEmrAutoTuneIntegrations,
    empty: emptyEmrAutoTune,
    canSave: canSaveEmrAutoTune,
    toPayload: (entry: EmrAutoTuneEntry) => ({
      ...entry,
      role_arn: entry.role_arn.trim(),
    }),
    Fields: EmrAutoTuneIntegrationForm,
    row: (entry: EmrAutoTuneEntry) => ({
      name: entry.role_arn,
      detail: "Auto-Tune role",
    }),
    // TODO: one role per tenant until clusters in several AWS accounts are supported.
    maxEntries: 1,
  },
];
